/**
 * FullAnalysisFold — the progressive-disclosure seam at the bottom of the
 * Customer Success cockpit.
 *
 * The Save-list at the top is the plan; EVERYTHING the plan was derived
 * from (churn explainability tables, playbooks, health rows, deliverables,
 * weekly updates) stays fully reachable behind this one fold. The children
 * remain MOUNTED while folded (display:none) so panel-local state — expanded
 * rows, draft previews, subscriptions wired by the parent — survives
 * toggling; nothing is lost, only tucked away.
 */

import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Override the toggle label, e.g. to enumerate what's inside. */
  label?: string;
}

export function FullAnalysisFold({ children, label = 'Show full analysis' }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <button
        type="button"
        className="glass-btn"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Everything the Save-list was derived from — churn tables, findings, health rows, deliverables, and weekly updates"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          width: 'fit-content',
          justifySelf: 'center',
        }}
      >
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        {open ? 'Hide full analysis' : label}
      </button>
      {/* Kept mounted — folded, never destroyed. */}
      <div style={{ display: open ? 'grid' : 'none', gap: 16 }}>{children}</div>
    </div>
  );
}
